import React, { useState, useEffect } from "react";
import { Check, Copy, Loader2 } from "lucide-react";
import { getIngredients } from "../api/ingredients";
import { useAuth } from "../context/AuthContext";
import CreateNewPLCustomerModal from "./CreateNewPLCustomerModal";

/* ------------------------------------------------------------------ */
/* Tiny inline icons                                                    */
/* ------------------------------------------------------------------ */
const PlusIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="13" height="13" {...props}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const TrashIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" {...props}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2" />
  </svg>
);
const InfoIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Reference Data for Lists!A:A and Lists!B:B (Clients -> PL Category)*/
/* ------------------------------------------------------------------ */
const PL_CLIENTS_DB = [
  { client: "Signature Jar", category: "Signature Items" },
  { client: "Youngevity", category: "Pouches" },
  { client: "Baker PL", category: "Baking" },
];

const BULK_PACK_TYPES = {
  "Bulk Master Case": { cs: 25, unit: "kg", multiplier: 1.0 },
  "Bulk 10 kg": { cs: 10, unit: "kg", multiplier: 1.05 },
  "Bulk 1 kg": { cs: 1, unit: "kg", multiplier: 1.15 },
};

const PACKAGING_CATALOG = {
  "Ergo Jar 212ml": { unitsPerCase: 6, netWeight: 140, unit: "g", packagingCost: 1.2 },
  "6 oz Round Glass": { unitsPerCase: 12, netWeight: 150, unit: "g", packagingCost: 0.95 },
  "Resealable Stand": { unitsPerCase: 150, netWeight: 90, unit: "g", packagingCost: 0.18 },
  "Pillow Bag Small": { unitsPerCase: 200, netWeight: 10, unit: "g", packagingCost: 0.12 },
  "PL-6 Round Bottle": { unitsPerCase: 100, netWeight: 100, unit: "g", packagingCost: 0.85 },
};

const DEFAULT_MARGIN_TIERS = {
  "Ergo Jar 212ml": 15,
  "6 oz Round Glass": 16,
  "Resealable Stand": 16,
  "Pillow Bag Small": 20,
  "PL-6 Round Bottle": 17,
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
const fmt = (v) => `$${Number(v).toFixed(2)}`;
const roundCharm = (v) => Math.ceil(v * 20) / 20;

const lookupPlMarginPercent = (mode, qty, plMarginTiers = [], defaultFallback = 20) => {
  if (!Array.isArray(plMarginTiers) || plMarginTiers.length === 0) return defaultFallback;
  const isBulk = mode === "bulk";
  const matchingTiers = plMarginTiers.filter(
    (t) => isBulk
      ? (t.category?.includes("Bulk") || t.pricingMode === "Bulk" || t.volumeBasis === "KG")
      : (t.category?.includes("Retail") || t.pricingMode === "Retail Packaging" || t.volumeBasis === "Units")
  );
  if (matchingTiers.length === 0) return defaultFallback;

  const numQty = Number(qty) || 0;
  const sorted = [...matchingTiers].sort((a, b) => (Number(a.fromQty) || 0) - (Number(b.fromQty) || 0));

  const found = sorted.find(
    (t) => numQty >= (Number(t.fromQty) || 0) && numQty <= (Number(t.toQty) || Number.MAX_SAFE_INTEGER)
  );

  if (found) return Number(found.targetNetMarginPercent) ?? defaultFallback;

  const lastTier = sorted[sorted.length - 1];
  return Number(lastTier.targetNetMarginPercent) ?? defaultFallback;
};

const convertWeightToKg = (qty, unit = "g") => {
  const val = Number(qty) || 0;
  const u = String(unit || "g").toLowerCase().trim();
  if (u === "g") return val / 1000;
  if (u === "kg") return val;
  if (u === "oz") return val * 0.0283495231;
  if (u === "lb" || u === "lbs") return val * 0.45359237;
  return val / 1000;
};

const getThroughputCosts = (packagingType, costingSettings) => {
  if (!costingSettings) return { ohCost: 0, labourCost: 0 };
  const { globalAssumptions, throughputOverhead = [] } = costingSettings;
  const row = throughputOverhead.find((t) => t.packagingType === packagingType);
  if (!row) return { ohCost: 0, labourCost: 0 };

  const workingDays = Math.max(1, Number(globalAssumptions?.workingDaysPerWeek) || 5);
  const weeklyOverhead = Math.max(0, Number(globalAssumptions?.weeklyOverheadCostCad) || 30200);
  const weeklyLabour = Math.max(0, Number(globalAssumptions?.weeklyLabourCostCad) || 38000);
  const throughputUnits = Math.max(0, Number(row.throughputUnitsPerDay) || 0);
  const unitsPerWeek = throughputUnits * workingDays;

  const ohCost = unitsPerWeek > 0 ? ((Number(row.overheadAllocationPercent) || 0) * weeklyOverhead) / unitsPerWeek : 0;
  const labourCost = unitsPerWeek > 0 ? ((Number(row.labourAllocationPercent) || 0) * weeklyLabour) / unitsPerWeek : 0;

  return { ohCost, labourCost };
};

const getCategoryPremiums = (clientCategory, costingSettings) => {
  if (!costingSettings || !clientCategory) return { sigPremium: 0, allergenPremium: 0 };
  const rules = costingSettings.categoryRules || [];
  const match = rules.find((r) => r.category === clientCategory);
  if (!match) return { sigPremium: 0, allergenPremium: 0 };

  return {
    sigPremium: Number(match.signaturePremiumPerUnit ?? match.signaturePremium ?? 0) || 0,
    allergenPremium: Number(match.allergenPremiumPerUnit ?? match.allergenPremium ?? 0) || 0,
  };
};

const getFxRate = (displayCurrency = "CAD", costingSettings) => {
  const curr = String(displayCurrency || "CAD").toUpperCase().trim();
  if (curr === "CAD") return 1.0;
  if (!costingSettings) return 1.0;
  const rates = costingSettings.fxRates || [];
  const match = rates.find((r) => r.currency === curr);
  return Number(match?.bufferedFxVsCad ?? match?.actualFxVsCad ?? 1.0) || 1.0;
};

let plRowId = 0;
const newBulkRow = () => ({
  id: `plb-${plRowId++}`,
  client: "",
  sku: "",
  packagingType: "Bulk Master Case",
  cs: 25,
  premium: "No",
  minimum: "No",
  unitCadOverride: "",
});
const newRetailRow = () => ({
  id: `plr-${plRowId++}`,
  client: "",
  sku: "",
  packagingType: "",
  loosePacking: "No",
  unitsPerCase: "",
  netWeight: "",
  netWeightUnit: "g",
  signaturePremium: "No",
  allergenPremium: "No",
  coPacking: "No",
  coPackFeeOverride: "",
});

/* ------------------------------------------------------------------ */
/* Shared style fragments                                              */
/* ------------------------------------------------------------------ */
const inputCls =
  "px-3 py-2 rounded-lg text-sm outline-none border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-black focus:border-transparent transition-colors w-full";
const thCls = "px-3 py-3 text-left font-semibold text-gray-700 whitespace-nowrap text-xs align-bottom";
const tdCls = "px-3 py-2 align-top";

/* ------------------------------------------------------------------ */
/* Header Component (Recipe Builder Style)                              */
/* ------------------------------------------------------------------ */
const HeaderWithApply = ({ title, colKey, copiedCol, onCopy, onApply, applyType = "none", options = [], placeholder = "" }) => {
  return (
    <div className="flex flex-col gap-1 w-full min-w-[5rem]">
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-gray-700">{title}</span>
        {onCopy && (
          <button
            type="button"
            onClick={() => onCopy(colKey)}
            className="text-gray-400 hover:text-gray-700 transition-colors"
            title={`Copy ${title} column`}
          >
            {copiedCol === colKey ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        {applyType === "select" && (
          <select
            className="ml-auto px-1.5 py-0.5 text-xs font-normal border border-gray-300 rounded bg-white text-gray-700 outline-none focus:ring-1 focus:ring-black"
            onChange={(e) => {
              if (e.target.value) {
                onApply(e.target.value);
                e.target.value = "";
              }
            }}
          >
            <option value="">{placeholder || "Set all..."}</option>
            {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
          </select>
        )}
      </div>
      {(applyType === "text" || applyType === "number") && (
        <input
          type={applyType}
          placeholder={placeholder || "Apply all (Enter)"}
          className="mt-1 w-full px-2 py-1 text-xs font-normal border border-gray-300 rounded bg-gray-50 text-gray-700 outline-none focus:ring-1 focus:ring-black"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value) {
              onApply(e.target.value);
              e.target.value = "";
            }
          }}
        />
      )}
    </div>
  );
};

const selectCls = `${inputCls} appearance-none bg-no-repeat pr-8`;
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
  backgroundPosition: "right 0.5rem center",
  backgroundSize: "1em",
};

/* ------------------------------------------------------------------ */
/* Bulk calculator                                                      */
/* ------------------------------------------------------------------ */
function BulkCalculator({ rmDatabase, plMarginTiers = [], costingSettings, canSeeDetailedCosts = true }) {
  const [rows, setRows] = useState([newBulkRow()]);
  const [manualMargin, setManualMargin] = useState(null);
  const [copiedCol, setCopiedCol] = useState(null);
  const [lineCount, setLineCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [targetRowIdForModal, setTargetRowIdForModal] = useState(null);
  const [localSettings, setLocalSettings] = useState(costingSettings);

  useEffect(() => {
    setLocalSettings(costingSettings);
  }, [costingSettings]);

  const totalBulkKg = React.useMemo(() => {
    return rows.reduce((sum, r) => {
      const packInfo = BULK_PACK_TYPES[r.packagingType];
      const csVal = Number(r.cs) || packInfo?.cs || 0;
      return sum + csVal;
    }, 0);
  }, [rows]);

  const volumeBasedMargin = lookupPlMarginPercent("bulk", totalBulkKg, plMarginTiers, 30);
  const targetMargin = manualMargin !== null ? manualMargin : volumeBasedMargin;

  const handleGenerateSKUs = async () => {
    const validRows = rows.map((r, i) => ({ ...r, index: i }))
                          .filter(r => r.sku && r.client);
    if (!validRows.length) {
      import("react-hot-toast").then((module) => module.default.error("No valid rows (need Client and SKU)."));
      return;
    }

    setGenerating(true);
    try {
      const { createPrivateLabelSku } = await import("../api/sku");
      const customerMappings = costingSettings?.privateLabelCustomerMappings || [];

      const payload = {
        variants: validRows.map(r => {
          const clientName = r.client;
          const formatSuffix = r.formatSuffix || "VRAC";
          const ingName = rmDatabase.find(db => db.sku === r.sku)?.name || r.sku;

          const mapping = customerMappings.find(m =>
            (m.clientName || m.customerName) === clientName &&
            (!formatSuffix || (m.formatSuffix || "").toLowerCase() === formatSuffix.toLowerCase())
          ) || customerMappings.find(m => (m.clientName || m.customerName) === clientName);

          const prefix = mapping?.skuPrefix || "1512";
          const suffix = formatSuffix || mapping?.formatSuffix || "VRAC";
          const productNameEN = [clientName, ingName, suffix].filter(Boolean).join(" ").toUpperCase();

          return {
            index: r.index,
            productNameEN,
            skuPrefix: prefix
          };
        })
      };
      const res = await createPrivateLabelSku(payload);
      if (res?.success) {
        setRows(prev => {
          const next = [...prev];
          res.variants.forEach(v => {
            if (next[v.index]) {
              next[v.index] = { ...next[v.index], generatedSku: v.sku, generatedUpc: v.upc };
            }
          });
          return next;
        });
        import("react-hot-toast").then((module) => module.default.success("SKUs generated!"));
      } else {
        import("react-hot-toast").then((module) => module.default.error("Failed to generate SKUs."));
      }
    } catch (e) {
      import("react-hot-toast").then((module) => module.default.error("Error generating SKUs."));
    } finally {
      setGenerating(false);
    }
  };


  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = (count = 1) => {
    const num = Math.max(1, Math.min(100, parseInt(count, 10) || 1));
    const newRows = Array.from({ length: num }, () => newBulkRow());
    setRows((prev) => [...prev, ...newRows]);
  };
  const clearAll = () => setRows([newBulkRow()]);
  const applyToAll = (key, val) => setRows((prev) => prev.map(r => ({ ...r, [key]: val })));

  const handleCopyCol = async (colKey, getVal) => {
    const text = rows.map(r => getVal(r)).join("\r\n");
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(colKey);
      setTimeout(() => setCopiedCol(null), 2000);
    } catch (err) { }
  };

  const handlePaste = (e, index) => {
    const pasteData = e.clipboardData.getData("text");
    if (!pasteData) return;
    const tokens = pasteData.split(/[\r\n\t,]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;

    e.preventDefault();
    const matched = tokens.map(token => {
      const found = rmDatabase.find(r => r.sku === token || (r.name || "").toLowerCase() === token.toLowerCase());
      return found ? found.sku : token;
    });

    setRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], sku: matched[0] };
      const extras = matched.slice(1).map(m => ({
        ...newBulkRow(),
        sku: m,
        client: newRows[index].client,
        packagingType: newRows[index].packagingType
      }));
      newRows.splice(index + 1, 0, ...extras);
      return newRows;
    });
  };

  const resolveClientCategory = (clientName) => {
    if (!clientName) return "";
    const match = PL_CLIENTS_DB.find(c => c.client === clientName);
    return match ? (match.category || "CREATE NEW") : "CREATE NEW";
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-6 shadow-sm min-w-0 w-full max-w-full overflow-hidden">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900">Private Label Calculator — Bulk</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Target margin %:</span>
            <input
              type="number"
              value={targetMargin}
              onChange={(e) => setManualMargin(Math.max(0, Math.min(95, Number(e.target.value) || 0)))}
              className={`${inputCls} w-20 text-center !py-1.5`}
            />
            {plMarginTiers.length > 0 && (
              <span className="text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap" title="Volume-based tier target">
                ({totalBulkKg} KG Tier: {volumeBasedMargin}%)
              </span>
            )}
          </div>
          <button
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors shadow-sm"
          >
            <TrashIcon /> Clear All
          </button>
          <button
            onClick={handleGenerateSKUs}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm disabled:opacity-50"
          >
            {generating ? "Generating..." : "Generate SKUs"}
          </button>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="1"
              max="100"
              value={lineCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setLineCount(isNaN(val) ? "" : Math.max(1, val));
              }}
              onBlur={() => {
                if (!lineCount || lineCount < 1) setLineCount(1);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") addRow(Number(lineCount) || 1);
              }}
              className={`${inputCls} w-16 text-center !py-1.5`}
              placeholder="1"
              title="Number of lines to add"
            />
            <button
              onClick={() => addRow(Number(lineCount) || 1)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
            >
              <PlusIcon /> Add Line
            </button>
          </div>
        </div>
      </div>
      <p className="text-sm mb-6 flex items-center gap-2 text-gray-600 bg-gray-50 p-3.5 rounded-xl border border-gray-200">
        <InfoIcon />
        Pick a client, an item, and a bulk pack size — cost, premium and minimum surcharges roll into the quoted price automatically.
      </p>

      <div className="overflow-x-auto border-t border-gray-200 -mx-6 px-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/50">
              <th className={thCls}>
                <HeaderWithApply
                  title="Client" colKey="client" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.client)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="PL Category" colKey="plCategory" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => resolveClientCategory(r.client))}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Ingredient Name" colKey="ingredientName" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => rmDatabase.find(db => db.sku === r.sku)?.name || "")}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Ingredient SKU" colKey="sku" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.sku)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Generated SKU" colKey="generatedSku" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.generatedSku)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Generated UPC" colKey="generatedUpc" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.generatedUpc)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Packaging" colKey="packagingType" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.packagingType)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="CS" colKey="cs" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.cs)}
                  onApply={(val) => applyToAll("cs", val)}
                  applyType="number" placeholder="Set all"
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Unit" colKey="unit" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => BULK_PACK_TYPES[r.packagingType]?.unit || "kg")}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Premium" colKey="premium" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.premium)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Minimum" colKey="minimum" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.minimum)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Unit CAD Override" colKey="unitCadOverride" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => r.unitCadOverride)}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply
                  title="Bulk / kg" colKey="bulkPerKg" copiedCol={copiedCol}
                  onCopy={(k) => handleCopyCol(k, r => {
                    const rm = rmDatabase.find((c) => c.sku === r.sku);
                    const packInfo = BULK_PACK_TYPES[r.packagingType];
                    const baseCost = rm ? rm.unitCost * (packInfo?.multiplier ?? 1) : null;
                    const unitCad = r.unitCadOverride !== "" ? Number(r.unitCadOverride) : baseCost != null ? Math.round((baseCost + (r.premium === "Yes" ? 0.75 : 0) + (r.minimum === "Yes" ? 0.3 : 0)) * 100) / 100 : null;
                    return unitCad != null ? roundCharm(unitCad / (1 - targetMargin / 100)) : "";
                  })}
                />
              </th>
              <th className={thCls}>
                <HeaderWithApply title="/ Master CS" />
              </th>
              <th className={thCls}>Margin %</th>
              <th className={`${thCls} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const rm = rmDatabase.find((r) => r.sku === row.sku);
              const packInfo = BULK_PACK_TYPES[row.packagingType];
              const rawBaseCost = rm ? (rm.unitCost || 0) * (packInfo?.multiplier ?? 1) : null;
              const baseCost = rawBaseCost;
              const premiumAdd = row.premium === "Yes" ? 0.75 : 0;
              const minimumAdd = row.minimum === "Yes" ? 0.3 : 0;
              const unitCad =
                row.unitCadOverride !== ""
                  ? Number(row.unitCadOverride)
                  : baseCost != null
                    ? Math.round((baseCost + premiumAdd + minimumAdd) * 100) / 100
                    : null;
              const bulkPerKg = unitCad != null ? roundCharm(unitCad / (1 - targetMargin / 100)) : null;
              const csVal = Number(row.cs) || packInfo?.cs || 0;
              const masterCs = bulkPerKg != null ? bulkPerKg * csVal : null;
              const marginPct = bulkPerKg != null && unitCad != null ? ((bulkPerKg - unitCad) / bulkPerKg) * 100 : null;
              const category = resolveClientCategory(row.client);

              return (
                <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                  <td className={tdCls}>
                    <select
                      value={row.client}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "CREATE NEW") {
                          setTargetRowIdForModal(row.id);
                          setIsCreateModalOpen(true);
                        } else {
                          updateRow(row.id, { client: val });
                        }
                      }}
                      className={`${selectCls} min-w-[11rem]`}
                      style={selectStyle}
                    >
                      <option value="">Select...</option>
                      {(((localSettings || costingSettings)?.privateLabelCustomerMappings && (localSettings || costingSettings).privateLabelCustomerMappings.length > 0)
                        ? (localSettings || costingSettings).privateLabelCustomerMappings
                        : PL_CLIENTS_DB
                      ).map((c, i) => {
                        const name = c.clientName || c.customerName || c.client;
                        return (
                          <option key={`${name}-${c.skuPrefix}-${i}`} value={name}>
                            {name} {c.skuPrefix ? `(Prefix: ${c.skuPrefix})` : ""}
                          </option>
                        );
                      })}
                      <option value="CREATE NEW">CREATE NEW</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input
                      value={category}
                      readOnly
                      placeholder="Category"
                      className={`${inputCls} min-w-[9rem] bg-gray-100 text-gray-500 cursor-not-allowed`}
                    />
                  </td>
                  <td className={tdCls}>
                    <input
                      list={`ingredient-list-bulk-${row.id}`}
                      value={row.tempIngredientName ?? (rm?.name || "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateRow(row.id, { tempIngredientName: val });
                        const match = rmDatabase.find(r => r.name === val);
                        if (match) updateRow(row.id, { sku: match.sku, tempIngredientName: undefined });
                      }}
                      onBlur={() => updateRow(row.id, { tempIngredientName: undefined })}
                      placeholder="Select ingredient..."
                      className={`${inputCls} min-w-[14rem]`}
                    />
                    <datalist id={`ingredient-list-bulk-${row.id}`}>
                      {rmDatabase.map(r => <option key={r.sku} value={r.name}>{r.name}</option>)}
                    </datalist>
                  </td>
                  <td className={tdCls}>
                    <input
                      value={row.sku}
                      onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                      onPaste={(e) => handlePaste(e, idx)}
                      placeholder="Paste or type SKU"
                      className={`${inputCls} font-mono min-w-[11rem] border-blue-200 focus:border-blue-500`}
                    />
                  </td>
                  <td className={tdCls}>
                    <input
                      value={row.generatedSku || ""}
                      readOnly
                      placeholder="Generated SKU"
                      className={`${inputCls} min-w-[10rem] bg-gray-100 text-gray-700 font-mono`}
                    />
                  </td>
                  <td className={tdCls}>
                    <input
                      value={row.generatedUpc || ""}
                      readOnly
                      placeholder="Generated UPC"
                      className={`${inputCls} min-w-[11rem] bg-gray-100 text-gray-700 font-mono`}
                    />
                  </td>
                  <td className={tdCls}>
                    <select
                      value={row.packagingType}
                      onChange={(e) => updateRow(row.id, { packagingType: e.target.value, cs: BULK_PACK_TYPES[e.target.value]?.cs || 0 })}
                      className={`${selectCls} min-w-[10rem]`}
                      style={selectStyle}
                    >
                      <option value="">Select packaging…</option>
                      {Object.keys(BULK_PACK_TYPES).map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input type="number" value={row.cs} onChange={(e) => updateRow(row.id, { cs: e.target.value })} className={`${inputCls} w-20 font-mono`} />
                  </td>
                  <td className={`${tdCls} text-sm text-gray-600 pt-4`}>{packInfo?.unit || "kg"}</td>
                  <td className={tdCls}>
                    <select value={row.premium} onChange={(e) => updateRow(row.id, { premium: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <select value={row.minimum} onChange={(e) => updateRow(row.id, { minimum: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </td>
                  <td className={tdCls}>
                    <input
                      type="number"
                      value={row.unitCadOverride}
                      onChange={(e) => updateRow(row.id, { unitCadOverride: e.target.value })}
                      placeholder={unitCad != null ? unitCad.toFixed(2) : "0.00"}
                      className={`${inputCls} w-24 font-mono`}
                    />
                  </td>
                  <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{bulkPerKg != null ? fmt(bulkPerKg) : "—"}</td>
                  <td className={`${tdCls} font-mono text-sm text-gray-800 pt-4`}>{masterCs != null ? fmt(masterCs) : "—"}</td>
                  <td className={`${tdCls} pt-3`}>
                    {marginPct != null ? (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 whitespace-nowrap">
                        {marginPct.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className={`${tdCls} text-right pt-3`}>
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(row.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateNewPLCustomerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        costingSettings={localSettings || costingSettings}
        onSuccess={(newMapping, allMappings) => {
          setLocalSettings((prev) => ({
            ...prev,
            privateLabelCustomerMappings: allMappings,
          }));
          if (targetRowIdForModal) {
            updateRow(targetRowIdForModal, {
              client: newMapping.clientName,
              formatSuffix: newMapping.formatSuffix,
            });
          }
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Retail packaging calculator                                         */
/* ------------------------------------------------------------------ */
function RetailPackagingCalculator({ rmDatabase, plMarginTiers = [], costingSettings = null, canSeeDetailedCosts = true }) {
  const [rows, setRows] = useState([newRetailRow()]);
  const [marginTiers, setMarginTiers] = useState(DEFAULT_MARGIN_TIERS);
  const [quoteVolume, setQuoteVolume] = useState(250);

  const volumeBasedMargin = lookupPlMarginPercent("retail", quoteVolume, plMarginTiers, 30);
  const [currency, setCurrency] = useState("CAD");
  const [copiedCol, setCopiedCol] = useState(null);
  const [lineCount, setLineCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [targetRowIdForModal, setTargetRowIdForModal] = useState(null);
  const [localSettings, setLocalSettings] = useState(costingSettings);

  useEffect(() => {
    setLocalSettings(costingSettings);
  }, [costingSettings]);

  const handleGenerateSKUs = async () => {
    const validRows = rows.map((r, i) => ({ ...r, index: i }))
                          .filter(r => r.sku && r.client && r.packagingType);
    if (!validRows.length) {
      import("react-hot-toast").then((module) => module.default.error("No valid rows (need Client, SKU, Packaging)."));
      return;
    }

    setGenerating(true);
    try {
      const { createPrivateLabelSku } = await import("../api/sku");
      const customerMappings = costingSettings?.privateLabelCustomerMappings || [];

      const payload = {
        variants: validRows.map(r => {
          const clientName = r.client;
          const formatSuffix = r.formatSuffix || "";
          const ingName = rmDatabase.find(db => db.sku === r.sku)?.name || r.sku;

          const mapping = customerMappings.find(m =>
            (m.clientName || m.customerName) === clientName &&
            (!formatSuffix || (m.formatSuffix || "").toLowerCase() === formatSuffix.toLowerCase())
          ) || customerMappings.find(m => (m.clientName || m.customerName) === clientName);

          const prefix = mapping?.skuPrefix || "";
          const suffix = formatSuffix || mapping?.formatSuffix || "";
          const weightStr = r.netWeight ? `${r.netWeight}${r.netWeightUnit || 'g'}` : "";
          const productNameEN = [clientName, ingName, weightStr, suffix].filter(Boolean).join(" ").toUpperCase();

          return {
            index: r.index,
            productNameEN,
            skuPrefix: prefix
          };
        })
      };
      const res = await createPrivateLabelSku(payload);
      if (res?.success) {
        setRows(prev => {
          const next = [...prev];
          res.variants.forEach(v => {
            if (next[v.index]) {
              next[v.index] = { ...next[v.index], generatedSku: v.sku, generatedUpc: v.upc };
            }
          });
          return next;
        });
        import("react-hot-toast").then((module) => module.default.success("SKUs generated!"));
      } else {
        import("react-hot-toast").then((module) => module.default.error("Failed to generate SKUs."));
      }
    } catch (e) {
      import("react-hot-toast").then((module) => module.default.error("Error generating SKUs."));
    } finally {
      setGenerating(false);
    }
  };

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = (count = 1) => {
    const num = Math.max(1, Math.min(100, parseInt(count, 10) || 1));
    const newRows = Array.from({ length: num }, () => newRetailRow());
    setRows((prev) => [...prev, ...newRows]);
  };
  const clearAll = () => setRows([newRetailRow()]);
  const applyToAll = (key, val) => setRows((prev) => prev.map(r => ({ ...r, [key]: val })));

  const handleCopyCol = async (colKey, getVal) => {
    const text = rows.map(r => getVal(r)).join("\r\n");
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(colKey);
      setTimeout(() => setCopiedCol(null), 2000);
    } catch (err) { }
  };

  const applyPackagingDefaults = (id, type) => {
    const pack = PACKAGING_CATALOG[type];
    if (!pack) return updateRow(id, { packagingType: type });
    updateRow(id, { packagingType: type, unitsPerCase: pack.unitsPerCase, netWeightUnit: pack.unit });
  };

  const handlePaste = (e, index) => {
    const pasteData = e.clipboardData.getData("text");
    if (!pasteData) return;
    const tokens = pasteData.split(/[\r\n\t,]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;

    e.preventDefault();
    const matched = tokens.map(token => {
      const found = rmDatabase.find(r => r.sku === token || (r.name || "").toLowerCase() === token.toLowerCase());
      return found ? found.sku : token;
    });

    setRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], sku: matched[0] };
      const extras = matched.slice(1).map(m => ({
        ...newRetailRow(),
        sku: m,
        client: newRows[index].client,
        packagingType: newRows[index].packagingType
      }));
      newRows.splice(index + 1, 0, ...extras);
      return newRows;
    });
  };

  const resolveClientCategory = (clientName) => {
    if (!clientName) return "";
    const list = (costingSettings?.privateLabelCustomerMappings && costingSettings.privateLabelCustomerMappings.length > 0)
      ? costingSettings.privateLabelCustomerMappings
      : PL_CLIENTS_DB;
    const match = list.find(c => (c.customerName || c.client) === clientName);
    return match ? (match.category || "CREATE NEW") : "CREATE NEW";
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-6 shadow-sm min-w-0 w-full max-w-full overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">Private Label Calculator — Retail Packaging</h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm whitespace-nowrap font-medium text-gray-700">Quote Volume:</span>
              <input
                type="number"
                value={quoteVolume}
                onChange={(e) => setQuoteVolume(Number(e.target.value) || 0)}
                className={`${inputCls} w-28 text-center font-mono !py-1.5`}
              />
              <span className="text-sm text-gray-500">units</span>
              {plMarginTiers.length > 0 && (
                <span className="text-xs font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap" title="Volume-based tier target">
                  Target Margin: {volumeBasedMargin}%
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Currency:</span>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${inputCls} !py-1.5`}>
                <option>CAD</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </div>
            <button
              onClick={clearAll}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors shadow-sm"
            >
              <TrashIcon /> Clear All
            </button>
            <button
              onClick={handleGenerateSKUs}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm disabled:opacity-50"
            >
              {generating ? "Generating..." : "Generate SKUs"}
            </button>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                max="100"
                value={lineCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setLineCount(isNaN(val) ? "" : Math.max(1, val));
                }}
                onBlur={() => {
                  if (!lineCount || lineCount < 1) setLineCount(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addRow(Number(lineCount) || 1);
                }}
                className={`${inputCls} w-16 text-center !py-1.5`}
                placeholder="1"
                title="Number of lines to add"
              />
              <button
                onClick={() => addRow(Number(lineCount) || 1)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
              >
                <PlusIcon /> Add Line
              </button>
            </div>
          </div>
        </div>
        <p className="text-sm mb-6 flex items-center gap-2 text-gray-600 bg-gray-50 p-3.5 rounded-xl border border-gray-200">
          <InfoIcon />
          Pick a packaging type to auto-fill Units/CS and Net Weight from Packaging Setup, then the target margin from PL Margin Tiers computes the retail price.
        </p>

        <div className="overflow-x-auto border-t border-gray-200 -mx-6 px-6 pb-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className={thCls}>
                  <HeaderWithApply
                    title="Client" colKey="client" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.client)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="PL Category" colKey="plCategory" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => resolveClientCategory(r.client))}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Ingredient Name" colKey="ingredientName" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => rmDatabase.find(db => db.sku === r.sku)?.name || "")}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Ingredient SKU" colKey="sku" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.sku)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Generated SKU" colKey="generatedSku" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.generatedSku)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Generated UPC" colKey="generatedUpc" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.generatedUpc)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Packaging" colKey="packagingType" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.packagingType)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Loose Packing?" colKey="loosePacking" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.loosePacking)}
                    onApply={(val) => applyToAll("loosePacking", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Units/CS" colKey="unitsPerCase" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.unitsPerCase)}
                    onApply={(val) => applyToAll("unitsPerCase", val)}
                    applyType="number" placeholder="Set all"
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Net Weight" colKey="netWeight" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.netWeight)}
                    onApply={(val) => applyToAll("netWeight", val)}
                    applyType="number" placeholder="Set all"
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Signature Premium" colKey="signaturePremium" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.signaturePremium)}
                    onApply={(val) => applyToAll("signaturePremium", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Allergen Premium" colKey="allergenPremium" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.allergenPremium)}
                    onApply={(val) => applyToAll("allergenPremium", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Co-Packing?" colKey="coPacking" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.coPacking)}
                    onApply={(val) => applyToAll("coPacking", val)}
                    applyType="select" options={["Yes", "No"]}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="Co-Pack Fee/Unit" colKey="coPackFeeOverride" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => r.coPackFeeOverride)}
                  />
                </th>
                <th className={thCls}>
                  <HeaderWithApply
                    title="FINAL COGS/Unit" colKey="finalCogsUnit" copiedCol={copiedCol}
                    onCopy={(k) => handleCopyCol(k, r => {
                      const rm = rmDatabase.find((c) => c.sku === r.sku);
                      if (!rm) return "";
                      const category = resolveClientCategory(r.client);
                      const pack = PACKAGING_CATALOG[r.packagingType];
                      const qtyKg = convertWeightToKg(r.netWeight, r.netWeightUnit);
                      const rmCost = (Number(rm.unitCost) || 0) * qtyKg;
                      const packCost = pack?.packagingCost ?? 0;
                      const { ohCost, labourCost } = getThroughputCosts(r.packagingType, costingSettings);
                      const coPackFee = r.coPacking === "Yes" ? (r.coPackFeeOverride !== "" ? Number(r.coPackFeeOverride) || 0 : 0) : (r.loosePacking === "Yes" ? 0.15 : 0);
                      const acctCost = rmCost + packCost;
                      const baseCost = acctCost + ohCost + labourCost + coPackFee;
                      const fxBuffer = acctCost * ((Number(costingSettings?.globalAssumptions?.fxBufferPercent) ?? 3) / 100);
                      const rmBuffer = acctCost * ((Number(costingSettings?.globalAssumptions?.cogsIncreaseBufferPercent) ?? 5) / 100);
                      const lossBuffer = baseCost * ((Number(costingSettings?.globalAssumptions?.lossBufferPercent) ?? 2) / 100);
                      const { sigPremium, allergenPremium } = getCategoryPremiums(category, costingSettings);
                      const sigAdd = r.signaturePremium === "Yes" ? sigPremium : 0;
                      const allAdd = r.allergenPremium === "Yes" ? allergenPremium : 0;
                      return (baseCost + fxBuffer + rmBuffer + lossBuffer + sigAdd + allAdd).toFixed(4);
                    })}
                  />
                </th>
                <th className={thCls}>FINAL COGS/CS</th>
                <th className={thCls}>PL Retail/Unit</th>
                <th className={thCls}>PL Retail/CS</th>
                <th className={thCls}>Profit Margin %</th>
                <th className={`${thCls} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const rm = rmDatabase.find((r) => r.sku === row.sku);
                const pack = PACKAGING_CATALOG[row.packagingType];
                const category = resolveClientCategory(row.client);

                let finalCogsUnit = null;
                let finalCogsCs = null;
                let retailUnit = null;
                let retailCs = null;
                let profitMarginPct = null;

                if (rm) {
                  const qtyKg = convertWeightToKg(row.netWeight, row.netWeightUnit);
                  const rmRatePerKg = Number(rm.unitCost) || 0;
                  const rmCost = rmRatePerKg * qtyKg;

                  const packCost = pack?.packagingCost ?? 0;
                  const { ohCost, labourCost } = getThroughputCosts(row.packagingType, costingSettings);

                  const coPackFee = row.coPacking === "Yes"
                    ? (row.coPackFeeOverride !== "" ? Number(row.coPackFeeOverride) || 0 : 0)
                    : (row.loosePacking === "Yes" ? 0.15 : 0);

                  const acctCost = rmCost + packCost;
                  const baseCost = acctCost + ohCost + labourCost + coPackFee;

                  const fxBufferPct = (Number(costingSettings?.globalAssumptions?.fxBufferPercent) ?? 3) / 100;
                  const rmBufferPct = (Number(costingSettings?.globalAssumptions?.cogsIncreaseBufferPercent) ?? 5) / 100;
                  const lossBufferPct = (Number(costingSettings?.globalAssumptions?.lossBufferPercent) ?? 2) / 100;

                  const fxBuffer = acctCost * fxBufferPct;
                  const rmBuffer = acctCost * rmBufferPct;
                  const lossBuffer = baseCost * lossBufferPct;

                  const { sigPremium, allergenPremium } = getCategoryPremiums(category, costingSettings);
                  const sigPremiumAdd = row.signaturePremium === "Yes" ? sigPremium : 0;
                  const allergenAdd = row.allergenPremium === "Yes" ? allergenPremium : 0;

                  const rawFinalCogsUnitCad = baseCost + fxBuffer + rmBuffer + lossBuffer + sigPremiumAdd + allergenAdd;
                  const finalCogsUnitCad = rawFinalCogsUnitCad;
                  finalCogsUnit = Math.round(finalCogsUnitCad * 10000) / 10000;

                  const unitsPerCase = Number(row.unitsPerCase) || pack?.unitsPerCase || 0;
                  finalCogsCs = finalCogsUnit * unitsPerCase;

                  const margin = plMarginTiers.length > 0 ? volumeBasedMargin : (marginTiers[row.packagingType] ?? 18);
                  const marginFrac = margin / 100;

                  const fxRate = getFxRate(currency, costingSettings);

                  const priceCad = finalCogsUnit / (1 - marginFrac);
                  const priceFx = priceCad * fxRate;
                  retailUnit = Math.ceil(priceFx * 20) / 20;

                  retailCs = retailUnit * unitsPerCase;

                  const sellCad = currency === "CAD" ? retailUnit : (retailUnit / fxRate);
                  profitMarginPct = sellCad > 0 ? ((sellCad - finalCogsUnit) / sellCad) * 100 : 0;

                  if (canSeeDetailedCosts) {
                    console.group(`🔍 [PL Retail Calc] Row #${idx + 1}: ${rm.name || row.sku} (${row.client || 'No Client'})`);
                    console.log(`Step 1: Net Weight Conversion -> ${row.netWeight} ${row.netWeightUnit || 'g'} = ${qtyKg.toFixed(6)} kg`);
                    console.log(`Step 2: Raw Material Cost -> Rate: $${rmRatePerKg.toFixed(4)}/kg × ${qtyKg.toFixed(6)} kg = $${rmCost.toFixed(4)}`);
                    console.log(`Step 3: Packaging Cost -> ${row.packagingType || 'None'}: $${packCost.toFixed(4)}`);
                    console.log(`Step 4: Accounting Cost (RM + Pack) -> $${rmCost.toFixed(4)} + $${packCost.toFixed(4)} = $${acctCost.toFixed(4)}`);
                    console.log(`Step 5: Throughput Overhead & Labour -> OH: $${ohCost.toFixed(4)}, Labour: $${labourCost.toFixed(4)}`);
                    console.log(`Step 6: Co-Pack Fee -> CoPacking="${row.coPacking}", Loose="${row.loosePacking}": $${coPackFee.toFixed(4)}`);
                    console.log(`Step 7: Base Cost (Acct + OH + Labour + CoPack) -> $${baseCost.toFixed(4)}`);
                    console.log(`Step 8: Global Buffers -> FX Buffer (${(fxBufferPct * 100).toFixed(1)}%): $${fxBuffer.toFixed(4)}, RM Buffer (${(rmBufferPct * 100).toFixed(1)}%): $${rmBuffer.toFixed(4)}, Loss Buffer (${(lossBufferPct * 100).toFixed(1)}%): $${lossBuffer.toFixed(4)}`);
                    console.log(`Step 9: Category Premiums -> Category: "${category}", Signature ("${row.signaturePremium || 'No'}"): $${sigPremiumAdd.toFixed(4)}, Allergen ("${row.allergenPremium || 'No'}"): $${allergenAdd.toFixed(4)}`);
                    console.log(`Step 10: FINAL COGS / Unit CAD -> Base ($${baseCost.toFixed(4)}) + FX Buff ($${fxBuffer.toFixed(4)}) + RM Buff ($${rmBuffer.toFixed(4)}) + Loss Buff ($${lossBuffer.toFixed(4)}) + Sig ($${sigPremiumAdd.toFixed(4)}) + Allg ($${allergenAdd.toFixed(4)}) = $${finalCogsUnit.toFixed(4)}`);
                    console.log(`Step 11: FINAL COGS / CS -> $${finalCogsUnit.toFixed(4)} × ${unitsPerCase} units/case = $${finalCogsCs.toFixed(2)}`);
                    console.log(`Step 12: Target Margin Lookup -> Quote Vol: ${quoteVolume} units -> Margin: ${margin}%`);
                    console.log(`Step 13: Price CAD (COGS / (1 - Margin)) -> $${finalCogsUnit.toFixed(4)} / (1 - ${marginFrac}) = $${priceCad.toFixed(4)}`);
                    console.log(`Step 14: Currency FX Rate -> ${currency} Rate: ${fxRate} -> Price ${currency}: $${priceFx.toFixed(4)}`);
                    console.log(`Step 15: PL Retail / Unit (CEILING to $0.05) -> $${retailUnit.toFixed(2)} ${currency}`);
                    console.log(`Step 16: PL Retail / CS -> $${retailUnit.toFixed(2)} × ${unitsPerCase} units = $${retailCs.toFixed(2)} ${currency}`);
                    console.log(`Step 17: Profit Margin % -> ($${sellCad.toFixed(4)} CAD - $${finalCogsUnit.toFixed(4)} CAD) / $${sellCad.toFixed(4)} CAD = ${profitMarginPct.toFixed(2)}%`);
                    console.groupEnd();
                  }
                }

                return (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                    <td className={tdCls}>
                      <select
                        value={row.client}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "CREATE NEW") {
                            setTargetRowIdForModal(row.id);
                            setIsCreateModalOpen(true);
                          } else {
                            updateRow(row.id, { client: val });
                          }
                        }}
                        className={`${selectCls} min-w-[11rem]`}
                        style={selectStyle}
                      >
                        <option value="">Select client...</option>
                        {(((localSettings || costingSettings)?.privateLabelCustomerMappings && (localSettings || costingSettings).privateLabelCustomerMappings.length > 0)
                          ? (localSettings || costingSettings).privateLabelCustomerMappings
                          : PL_CLIENTS_DB
                        ).map((c, i) => {
                          const name = c.clientName || c.customerName || c.client;
                          return (
                            <option key={`${name}-${c.skuPrefix}-${i}`} value={name}>
                              {name} {c.skuPrefix ? `(Prefix: ${c.skuPrefix})` : ""}
                            </option>
                          );
                        })}
                        <option value="CREATE NEW">CREATE NEW</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <input
                        value={category}
                        readOnly
                        placeholder="Category"
                        className={`${inputCls} min-w-[9rem] bg-gray-100 text-gray-500 cursor-not-allowed`}
                      />
                    </td>
                    <td className={tdCls}>
                      <input
                        list={`ingredient-list-retail-${row.id}`}
                        value={row.tempIngredientName ?? (rm?.name || "")}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateRow(row.id, { tempIngredientName: val });
                          const match = rmDatabase.find(r => r.name === val);
                          if (match) updateRow(row.id, { sku: match.sku, tempIngredientName: undefined });
                        }}
                        onBlur={() => updateRow(row.id, { tempIngredientName: undefined })}
                        placeholder="Select ingredient..."
                        className={`${inputCls} min-w-[14rem]`}
                      />
                      <datalist id={`ingredient-list-retail-${row.id}`}>
                        {rmDatabase.map(r => <option key={r.sku} value={r.name}>{r.name}</option>)}
                      </datalist>
                    </td>
                    <td className={tdCls}>
                      <input
                        value={row.sku}
                        onChange={(e) => updateRow(row.id, { sku: e.target.value })}
                        onPaste={(e) => handlePaste(e, idx)}
                        placeholder="Paste or type SKU"
                        className={`${inputCls} font-mono min-w-[11rem] border-blue-200 focus:border-blue-500`}
                      />
                    </td>
                    <td className={tdCls}>
                      <input
                        value={row.generatedSku || ""}
                        readOnly
                        placeholder="Generated SKU"
                        className={`${inputCls} min-w-[10rem] bg-gray-100 text-gray-700 font-mono`}
                      />
                    </td>
                    <td className={tdCls}>
                      <input
                        value={row.generatedUpc || ""}
                        readOnly
                        placeholder="Generated UPC"
                        className={`${inputCls} min-w-[11rem] bg-gray-100 text-gray-700 font-mono`}
                      />
                    </td>
                    <td className={tdCls}>
                      <select
                        value={row.packagingType}
                        onChange={(e) => applyPackagingDefaults(row.id, e.target.value)}
                        className={`${selectCls} min-w-[11rem]`}
                        style={selectStyle}
                      >
                        <option value="">Select packaging…</option>
                        {Object.keys(PACKAGING_CATALOG).map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                    </td>
                    <td className={tdCls}>
                      <select value={row.loosePacking} onChange={(e) => updateRow(row.id, { loosePacking: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <input
                        type="number"
                        value={row.unitsPerCase}
                        onChange={(e) => updateRow(row.id, { unitsPerCase: e.target.value })}
                        placeholder={pack ? String(pack.unitsPerCase) : "0"}
                        className={`${inputCls} w-20 font-mono`}
                      />
                    </td>
                    <td className={`${tdCls} min-w-[10rem]`}>
                      <div className="flex items-center gap-1.5 w-full">
                        <input
                          type="number"
                          step="any"
                          value={row.netWeight}
                          onChange={(e) => updateRow(row.id, { netWeight: e.target.value })}
                          placeholder="0"
                          className={`${inputCls} flex-1 font-mono min-w-[4.5rem] !py-1.5`}
                        />
                        <select
                          value={row.netWeightUnit || "g"}
                          onChange={(e) => updateRow(row.id, { netWeightUnit: e.target.value })}
                          className="text-xs font-semibold bg-gray-50 border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-black"
                        >
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                          <option value="oz">oz</option>
                          <option value="lb">lb</option>
                        </select>
                      </div>
                    </td>
                    <td className={tdCls}>
                      <select value={row.signaturePremium || "No"} onChange={(e) => updateRow(row.id, { signaturePremium: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <select value={row.allergenPremium || "No"} onChange={(e) => updateRow(row.id, { allergenPremium: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      <select value={row.coPacking} onChange={(e) => updateRow(row.id, { coPacking: e.target.value })} className={`${selectCls} w-20`} style={selectStyle}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td className={tdCls}>
                      {row.coPacking === "Yes" ? (
                        <input
                          type="number"
                          value={row.coPackFeeOverride}
                          onChange={(e) => updateRow(row.id, { coPackFeeOverride: e.target.value })}
                          placeholder="0.00"
                          className={`${inputCls} w-20 font-mono`}
                        />
                      ) : (
                        <span className="text-gray-400 pl-4">—</span>
                      )}
                    </td>
                    <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{finalCogsUnit != null ? fmt(finalCogsUnit) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm text-gray-600 pt-4`}>{finalCogsCs != null ? fmt(finalCogsCs) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm font-semibold text-gray-900 pt-4`}>{retailUnit != null ? fmt(retailUnit) : "—"}</td>
                    <td className={`${tdCls} font-mono text-sm text-gray-800 pt-4`}>{retailCs != null ? fmt(retailCs) : "—"}</td>
                    <td className={`${tdCls} pt-3`}>
                      {profitMarginPct != null ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700">
                          {profitMarginPct.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className={`${tdCls} text-right pt-3`}>
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(row.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                          <TrashIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <CreateNewPLCustomerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        costingSettings={localSettings || costingSettings}
        onSuccess={(newMapping, allMappings) => {
          setLocalSettings((prev) => ({
            ...prev,
            privateLabelCustomerMappings: allMappings,
          }));
          if (targetRowIdForModal) {
            updateRow(targetRowIdForModal, {
              client: newMapping.clientName,
              formatSuffix: newMapping.formatSuffix,
            });
          }
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Main exported component                                             */
/* ------------------------------------------------------------------ */
const PrivateLabel = () => {
  const { user, canRead, getAccess } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [mode, setMode] = useState("retail");
  const [rmDatabase, setRmDatabase] = useState([]);
  const [costingSettings, setCostingSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const detailedAccess = getAccess ? getAccess("CostingDetailedCosts") : (canRead("CostingDetailedCosts") ? "read" : "no_access");
  const canSeeDetailedCosts =
    detailedAccess === "no_access"
      ? false
      : detailedAccess === "read" || detailedAccess === "write"
      ? true
      : isAdmin;

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getIngredients().catch(() => []),
      import("../api/costingSettingsApi").then((m) => m.getCostingSettings()).catch(() => null),
    ]).then(([ingData, settingsRes]) => {
      if (mounted) {
        setRmDatabase(ingData || []);
        if (settingsRes?.data) {
          setCostingSettings(settingsRes.data);
        }
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center bg-transparent">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-sm text-gray-500">Loading PL Calculator Data...</p>
        </div>
      </div>
    );
  }

  const plMarginTiers = costingSettings?.privateLabelMarginTiers || [];

  return (
    <div className="space-y-6 font-sans">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Private Label Calculator</h2>
        <p className="text-sm text-gray-500 mt-1">
          Bulk and retail-packaging quoting for private label clients.
        </p>
      </div>

      <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-xl w-fit border border-gray-200">
        {[
          { key: "retail", label: "Retail Packaging" },
          { key: "bulk", label: "Bulk" },
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === m.key
              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === "bulk" ? (
        <BulkCalculator rmDatabase={rmDatabase} plMarginTiers={plMarginTiers} costingSettings={costingSettings} canSeeDetailedCosts={canSeeDetailedCosts} />
      ) : (
        <RetailPackagingCalculator rmDatabase={rmDatabase} plMarginTiers={plMarginTiers} costingSettings={costingSettings} canSeeDetailedCosts={canSeeDetailedCosts} />
      )}
    </div>
  );
};

export default PrivateLabel;